import apiService from "../service/apiService";
import apiConfig, {
  buildEndpointUrl,
  PROXY_CONFIG,
  LOGGING_CONFIG,
} from "config/apiConfig";
import { appConfig } from "config/keycloak";
// COMENTADO - Solo usar token de Keycloak, no AuthService para client_credentials
// import authService from "../service/AuthService.ts";
import axios from "axios";

// Helper function for parsing Content-Disposition header
const parseContentDisposition = (headerValue) => {
  if (!headerValue) return undefined;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
    headerValue
  );
  try {
    const encoded = match?.[1];
    if (encoded) return decodeURIComponent(encoded);
    const simple = match?.[2];
    return simple || undefined;
  } catch (_) {
    return undefined;
  }
};

// Helper function for inferring filename
const inferFilename = (details, documentId, contentType) => {
  if (details?.name) return details.name;
  const extByType = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/gif": "gif",
    "image/svg+xml": "svg",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "text/plain": "txt",
    "text/csv": "csv",
  };
  const ext = extByType[contentType] || "bin";
  return `esign-document-${documentId}.${ext}`;
};

// Helper function for checking transient errors
const isTransientError = (status) => {
  return (
    status === 408 ||
    status === 425 ||
    status === 429 ||
    (status >= 500 && status <= 599)
  );
};

// Helper function for sleep/delay
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class EsignService {
  constructor() {
    // Usa configuración centralizada: si baseUrl está vacío (entorno local) usa proxy, sino usa URL directa
    this.api = axios.create({
      // baseURL: "http://localhost:8082" // Para desarrollo local del servicio
      baseURL: appConfig.baseUrl, // Usa configuración centralizada de keycloak.js
    });
  }

  // Get document tracking by reference ID and document type
  async getContractByReferenceId(
    token,
    dealPublicId,
    documentType = "CONTRACT"
  ) {
    // documentType is required by the backend endpoint
    if (!documentType) {
      throw new Error("documentType parameter is required");
    }

    const params = new URLSearchParams();
    params.append("documentType", documentType);

    // dealPublicId = 1015; //Just for testing

    console.log("Get contract with reference id: " + dealPublicId + " and document type: " + documentType);
    const url = `/esign-service/api/document-tracking/reference/${dealPublicId}?${params.toString()}`;

    return this.api.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  // Auxiliary method: Download completed contract by envelope ID
  // This endpoint downloads the completed contract file using the envelope ID
  async downloadCompletedContractByEnvelopId(token, envelopeId) {
    return this.api.get(`/esign-service/api/envelopes/download/${envelopeId}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob", // Important for file downloads
    });
  }

  // Get presigned URL for PDF download
  async getPresignedDownloadUrl(documentId, { signal } = {}) {
    if (!documentId) throw new Error("documentId is required");

    // Use the esign-service endpoint pattern
    const endpoint = `/esign-service/api/documents/${documentId}/download`;

    try {
      // Get token using the same pattern as documentsApi
      let token = null;
      try {
        const userToken =
          typeof window !== "undefined" && window.__kc_getToken
            ? window.__kc_getToken()
            : null;
        token = userToken || (await apiService.getToken());
      } catch (_) {
        token = await apiService.getToken();
      }

      const response = await this.api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      const data = response.data;

      // Adapter for presigned URL response
      const url = data?.presignedUrl || data?.url || data?.downloadUrl;
      if (!url) {
        throw new Error("Response does not contain a presigned URL");
      }

      return {
        url,
        filename: data?.fileName || data?.filename || data?.name,
        expiresAt: data?.expiresAt || data?.expires_at,
        ttlSeconds: data?.ttlSeconds || data?.ttl_seconds,
        raw: data,
      };
    } catch (error) {
      if (LOGGING_CONFIG.enabled) {
        console.error("Error getting presigned download URL:", error);
      }
      throw error;
    }
  }

  // Get envelope history by envelope ID (updated endpoint)
  async getHistory(token, envelopeId) {
    return this.api.get(
      `/esign-service/api/envelope-status/envelope/${envelopeId}/history`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  // Get webhook history by envelope ID (new webhook endpoint)
  async getWebhookHistory(token, envelopeId) {
    return this.api.get(
      `/esign-service/webhook/envelope/${envelopeId}/history`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  // Download PDF directly as blob
  async downloadPdfAsBlob(documentId, { signal, retryAttempts = 2 } = {}) {
    if (!documentId) throw new Error("documentId is required");

    const endpoint = `/esign-service/api/documents/${documentId}/download`;

    const doOnce = async () => {
      try {
        // Get token using the same pattern as documentsApi
        let token = null;
        try {
          const userToken =
            typeof window !== "undefined" && window.__kc_getToken
              ? window.__kc_getToken()
              : null;
          token = userToken || (await apiService.getToken());
        } catch (_) {
          token = await apiService.getToken();
        }

        const response = await this.api.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          signal,
        });

        const blob = response.data;
        const contentType =
          response.headers["content-type"] || "application/pdf";
        const contentDisposition = response.headers["content-disposition"];
        const filename =
          parseContentDisposition(contentDisposition) ||
          inferFilename(null, documentId, contentType);

        return {
          blob,
          filename,
          contentType,
          size: blob.size,
          objectUrl: URL.createObjectURL(blob), // For preview/display
        };
      } catch (error) {
        if (LOGGING_CONFIG.enabled) {
          console.error("Error downloading PDF as blob:", error);
        }
        throw error;
      }
    };

    // Retry logic for transient errors
    let attempt = 0;
    let lastError = null;
    while (attempt <= retryAttempts) {
      try {
        return await doOnce();
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        if (
          error?.code === "ERR_CANCELED" ||
          !isTransientError(status) ||
          attempt === retryAttempts
        ) {
          throw error;
        }
        const backoffMs = 500 * Math.pow(2, attempt); // 500ms, 1000ms, 2000ms
        if (LOGGING_CONFIG.enabled) {
          console.warn(
            `Transient error (status ${status}). Retrying in ${backoffMs}ms...`
          );
        }
        await sleep(backoffMs);
        attempt += 1;
      }
    }
    throw lastError || new Error("Unknown download failure");
  }

  // Download PDF using apiService requestWithBlob (alternative approach)
  async downloadPdfWithApiService(documentId, { signal } = {}) {
    if (!documentId) throw new Error("documentId is required");

    const endpoint = `/esign-service/api/documents/${documentId}/download`;

    try {
      // Use apiService's requestWithBlob method for consistency with documentsApi
      const response = await apiService.requestWithBlob(endpoint, {
        method: "GET",
        signal,
        redirect: "manual",
      });

      // Handle redirect to presigned URL (302/301)
      if (response.status === 302 || response.status === 301) {
        const location =
          response.headers.get("Location") || response.headers.get("location");
        if (location) return { url: location };
      }

      // Handle successful response
      if (response.ok) {
        const contentType =
          response.headers.get("content-type") || "application/pdf";

        // If JSON response with URL
        if (contentType.includes("application/json")) {
          const data = await response.json().catch(() => ({}));
          const url = data?.presignedUrl || data?.url || data?.downloadUrl;
          const filename = data?.fileName || data?.filename;
          if (url)
            return {
              url,
              filename,
              expiresAt: data?.expiresAt,
              ttlSeconds: data?.ttlSeconds,
            };
          throw new Error("JSON response does not contain a download URL");
        } else {
          // If blob/binary response
          const blob = await response.blob();
          const contentDisposition =
            response.headers.get("Content-Disposition") ||
            response.headers.get("content-disposition");
          const filename =
            parseContentDisposition(contentDisposition) ||
            inferFilename(null, documentId, contentType);

          return {
            blob,
            filename,
            contentType,
            objectUrl: URL.createObjectURL(blob),
            size: blob.size,
          };
        }
      }

      // Error handling
      const text = await response.text().catch(() => "");
      const error = new Error(
        `HTTP ${response.status}: ${text || "Error downloading PDF"}`
      );
      error.status = response.status;
      throw error;
    } catch (error) {
      if (LOGGING_CONFIG.enabled) {
        console.error("Error downloading PDF with apiService:", error);
      }
      throw error;
    }
  }

  // Execute a function with fresh presigned URL; retry once if URL expires
  async withFreshUrl(documentId, consumerFn) {
    const first = await this.getPresignedDownloadUrl(documentId);
    try {
      return await consumerFn(first);
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) {
        const second = await this.getPresignedDownloadUrl(documentId);
        return consumerFn(second);
      }
      throw e;
    }
  }

  // Convenience method to trigger browser download
  async downloadPdfToDevice(documentId, { filename, signal } = {}) {
    try {
      const result = await this.downloadPdfAsBlob(documentId, { signal });

      // Use provided filename or fallback to the one from the response
      const downloadFilename = filename || result.filename;

      // Create download link and trigger download
      const link = document.createElement("a");
      link.href = result.objectUrl;
      link.download = downloadFilename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL after a short delay to allow download to start
      setTimeout(() => {
        URL.revokeObjectURL(result.objectUrl);
      }, 100);

      return {
        filename: downloadFilename,
        size: result.size,
        contentType: result.contentType,
      };
    } catch (error) {
      if (LOGGING_CONFIG.enabled) {
        console.error("Error downloading PDF to device:", error);
      }
      throw error;
    }
  }
}

export default new EsignService();
